// src/features/auth/login/pages/LoginPage.tsx

import React, {
  useEffect,
  useState,
} from 'react';

import axios from 'axios';

import {
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  useNavigate,
} from 'react-router-dom';

import type {
  AppDispatch,
  RootState,
} from '../../../../app/store';

import {
  clearTokens,
  setTokens,
  setUser,
} from '../../authSlice';

import type {
  LoginRequest,
} from '../types/login.types';

import {
  loginService,
} from '../services/LoginService';

import {
  memberService,
} from '../../../member/services/memberService';

import RouteConfig
  from '../../../../routes/RouteConfig';


type ApiErrorPayload = {
  message?: string;
};


const getErrorMessage = (
  error: unknown,
  fallback: string,
): string => {

  if (
    axios.isAxiosError(
      error,
    )
  ) {

    const payload =
      error.response?.data as
        | ApiErrorPayload
        | undefined;


    return (
      payload?.message ||
      error.message ||
      fallback
    );
  }


  if (
    error instanceof Error
  ) {

    return (
      error.message ||
      fallback
    );
  }


  return fallback;
};


export default function LoginPage():
  React.JSX.Element {

  const navigate =
    useNavigate();


  const dispatch =
    useDispatch<AppDispatch>();


  /**
   * =========================================================
   * AuthInitializer에서 복원된 로그인 사용자
   * =========================================================
   */
  const authUser =
    useSelector(
      (
        state:
          RootState,
      ) =>
        state.auth.user,
    );


  const accessToken =
    useSelector(
      (
        state:
          RootState,
      ) =>
        state.auth.accessToken,
    );


  const [
    userId,
    setUserId,
  ] =
    useState('');


  const [
    password,
    setPassword,
  ] =
    useState('');


  const [
    errorMessage,
    setErrorMessage,
  ] =
    useState('');


  const [
    isSubmitting,
    setIsSubmitting,
  ] =
    useState(false);


  /**
   * =========================================================
   * 권한별 홈 이동
   * =========================================================
   */
  const navigateToRoleHome = (
    role?: string,
  ): void => {

    switch (
      role
    ) {

      case 'ADMIN':

        navigate(
          RouteConfig
            .Management
            .AdminHome
            .fullPath,
          {
            replace:
              true,
          },
        );

        return;


      case 'SUPERADMIN':

        navigate(
          RouteConfig
            .SuperAdmin
            .Home
            .fullPath,
          {
            replace:
              true,
          },
        );

        return;


      case 'USER':

      default:

        navigate(
          RouteConfig
            .User
            .Home
            .fullPath,
          {
            replace:
              true,
          },
        );
    }
  };


  /**
   * =========================================================
   * 이미 로그인된 사용자
   *
   * AuthInitializer가 자동 로그인을 복원한 상태에서
   * /auth/login으로 접근하면
   * 로그인 화면을 다시 보여주지 않고
   * 권한별 홈으로 이동합니다.
   * =========================================================
   */
  useEffect(
    () => {

      if (
        !accessToken ||
        !authUser
      ) {
        return;
      }


      navigateToRoleHome(
        authUser.role,
      );

    },
    [
      accessToken,
      authUser,
      // navigateToRoleHome은 컴포넌트 내부 함수지만
      // navigate와 auth 상태가 변경될 때만
      // 실질적으로 필요한 Effect입니다.
    ],
  );


  /**
   * =========================================================
   * 로그인
   * =========================================================
   */
  const handleLogin =
    async (): Promise<void> => {

      const trimmedUserId =
        userId.trim();


      setErrorMessage(
        '',
      );


      /**
       * 입력값 검증
       */
      if (
        !trimmedUserId
      ) {

        setErrorMessage(
          '아이디를 입력해주세요.',
        );


        return;
      }


      if (
        !password
      ) {

        setErrorMessage(
          '비밀번호를 입력해주세요.',
        );


        return;
      }


      const loginData:
        LoginRequest = {

        userId:
          trimmedUserId,

        password,
      };


      /**
       * 로그인 성공 후
       * 토큰까지 저장되었는지 여부
       *
       * 이후 /users/me가 실패했을 때
       * 불완전한 로그인 상태를 정리하기 위해 사용합니다.
       */
      let tokensStored =
        false;


      try {

        setIsSubmitting(
          true,
        );


        /**
         * =====================================================
         * 1. 로그인
         *
         * Access Token
         * Refresh Token 발급
         * =====================================================
         */
        const response =
          await loginService
            .login(
              loginData,
            );


        const tokenData =
          response.data?.data;


        if (
          !tokenData
            ?.accessToken
        ) {

          throw new Error(
            '응답에 Access Token이 없습니다.',
          );
        }


        if (
          !tokenData
            ?.refreshToken
        ) {

          throw new Error(
            '응답에 Refresh Token이 없습니다.',
          );
        }


        /**
         * =====================================================
         * 2. Redux 토큰 저장
         *
         * authSlice.setTokens()
         *
         * accessToken
         * -> Redux
         *
         * refreshToken
         * -> Redux
         * -> localStorage
         *
         * 따라서 이후 브라우저를 다시 열어도
         * AuthInitializer가 자동 로그인 복구 가능
         * =====================================================
         */
        dispatch(
          setTokens(
            tokenData,
          ),
        );


        tokensStored =
          true;


        /**
         * =====================================================
         * 3. 로그인 사용자 조회
         *
         * GET /api/v2/users/me
         *
         * axios request interceptor가
         * 방금 저장한 accessToken을
         * Authorization에 자동 추가
         * =====================================================
         */
        const userData =
          await memberService
            .getMyInfo();


        if (
          !userData
        ) {

          throw new Error(
            '로그인 사용자 정보를 확인할 수 없습니다.',
          );
        }


        /**
         * =====================================================
         * 4. Redux 사용자 저장
         * =====================================================
         */
        dispatch(
          setUser(
            userData,
          ),
        );


        /**
         * =====================================================
         * 5. 권한별 홈 이동
         *
         * USER
         * → /user/home
         *
         * ADMIN
         * → /management/home
         *
         * SUPERADMIN
         * → /superadmin/home
         * =====================================================
         */
        navigateToRoleHome(
          userData.role,
        );

      } catch (
        error: unknown
      ) {

        /**
         * =====================================================
         * 로그인 토큰은 발급됐지만
         * 사용자 정보 복원 단계에서 실패했다면
         *
         * Refresh Token이 localStorage에
         * 어정쩡하게 남는 것을 방지
         * =====================================================
         */
        if (
          tokensStored
        ) {

          dispatch(
            clearTokens(),
          );
        }


        /**
         * =====================================================
         * 로그인 실패
         * =====================================================
         */
        if (
          axios.isAxiosError(
            error,
          )
        ) {

          const status =
            error.response
              ?.status;


          if (
            status ===
            404
          ) {

            setErrorMessage(
              '존재하지 않는 사용자입니다.',
            );


            return;
          }


          if (
            status ===
            400
          ) {

            setErrorMessage(
              '아이디 또는 비밀번호를 확인해주세요.',
            );


            return;
          }


          if (
            status ===
            401
          ) {

            setErrorMessage(
              '아이디 또는 비밀번호를 확인해주세요.',
            );


            return;
          }


          if (
            status ===
            403
          ) {

            setErrorMessage(
              '로그인할 수 없는 계정입니다.',
            );


            return;
          }
        }


        setErrorMessage(
          getErrorMessage(
            error,
            '로그인에 실패했습니다.',
          ),
        );

      } finally {

        setIsSubmitting(
          false,
        );
      }
    };


  /**
   * =========================================================
   * Form Submit
   * =========================================================
   */
  const handleSubmit = (
    event:
      React.FormEvent<HTMLFormElement>,
  ): void => {

    event.preventDefault();


    if (
      isSubmitting
    ) {
      return;
    }


    void handleLogin();
  };


  return (
    <main
      className="
        min-h-screen
        bg-gray-100
        md:grid
        md:grid-cols-2
        lg:grid-cols-[3fr_2fr]
      "
    >

      {/* =========================================
          태블릿 / 데스크톱 로고 영역
          ========================================= */}

      <section
        className="
          relative
          hidden
          min-h-screen
          overflow-hidden
          bg-zinc-950
          md:flex
          md:items-center
          md:justify-center
        "
      >

        <div
          className="
            absolute
            inset-0
            bg-gradient-to-br
            from-black/10
            via-transparent
            to-emerald-950/30
          "
          aria-hidden="true"
        />


        <div
          className="
            relative
            z-10
            w-full
            max-w-2xl
            px-10
          "
        >

          <img
            src="/login.png"
            alt="LAPOIT"
            className="
              mx-auto
              h-auto
              w-full
              max-w-lg
              object-contain
            "
          />

        </div>

      </section>


      {/* =========================================
          로그인 영역
          ========================================= */}

      <section
        className="
          flex
          min-h-screen
          items-center
          justify-center
          px-5
          py-8
          sm:px-8
          md:px-10
          md:py-10
          lg:px-14
        "
      >

        <div
          className="
            w-full
            max-w-md
          "
        >

          {/* HEADER */}

          <header
            className="
              mb-8
            "
          >

            <span
              className="
                text-xs
                font-bold
                tracking-[0.2em]
                text-main
              "
            >
              LAPOIT MEMBER
            </span>


            <h1
              className="
                mt-3
                text-3xl
                font-bold
                text-gray-900
                sm:text-4xl
              "
            >
              로그인
            </h1>

          </header>


          {/* FORM */}

          <form
            className="
              space-y-5
            "
            onSubmit={
              handleSubmit
            }
            noValidate
          >

            {/* USER ID */}

            <div>

              <label
                htmlFor="userId"
                className="
                  mb-2
                  block
                  text-sm
                  font-semibold
                  text-gray-700
                "
              >
                아이디
              </label>


              <input
                id="userId"
                name="userId"
                type="text"
                value={
                  userId
                }
                placeholder="아이디를 입력해주세요"
                autoComplete="username"
                disabled={
                  isSubmitting
                }
                onChange={(
                  event,
                ) => {

                  setUserId(
                    event.target.value,
                  );


                  if (
                    errorMessage
                  ) {

                    setErrorMessage(
                      '',
                    );
                  }
                }}
                className="
                  w-full
                  rounded-xl
                  border
                  border-gray-300
                  bg-white
                  px-4
                  py-3
                  text-base
                  text-gray-900
                  outline-none
                  transition
                  placeholder:text-gray-400
                  focus:border-main
                  focus:ring-2
                  focus:ring-main/20
                  disabled:cursor-not-allowed
                  disabled:bg-gray-100
                "
              />

            </div>


            {/* PASSWORD */}

            <div>

              <label
                htmlFor="password"
                className="
                  mb-2
                  block
                  text-sm
                  font-semibold
                  text-gray-700
                "
              >
                비밀번호
              </label>


              <input
                id="password"
                name="password"
                type="password"
                value={
                  password
                }
                placeholder="비밀번호를 입력해주세요"
                autoComplete="current-password"
                disabled={
                  isSubmitting
                }
                onChange={(
                  event,
                ) => {

                  setPassword(
                    event.target.value,
                  );


                  if (
                    errorMessage
                  ) {

                    setErrorMessage(
                      '',
                    );
                  }
                }}
                className="
                  w-full
                  rounded-xl
                  border
                  border-gray-300
                  bg-white
                  px-4
                  py-3
                  text-base
                  text-gray-900
                  outline-none
                  transition
                  placeholder:text-gray-400
                  focus:border-main
                  focus:ring-2
                  focus:ring-main/20
                  disabled:cursor-not-allowed
                  disabled:bg-gray-100
                "
              />

            </div>


            {/* ERROR */}

            {errorMessage && (

              <div
                role="alert"
                aria-live="polite"
                className="
                  rounded-lg
                  bg-red-50
                  px-4
                  py-3
                  text-sm
                  leading-5
                  text-red-600
                "
              >
                {errorMessage}
              </div>

            )}


            {/* LOGIN BUTTON */}

            <button
              type="submit"
              disabled={
                isSubmitting
              }
              className="
                flex
                w-full
                items-center
                justify-center
                rounded-xl
                bg-main
                px-5
                py-3
                font-semibold
                text-white
                transition
                hover:bg-main-medium
                focus:outline-none
                focus:ring-2
                focus:ring-main/30
                focus:ring-offset-2
                disabled:cursor-not-allowed
                disabled:opacity-60
              "
            >

              {isSubmitting ? (

                <span
                  className="
                    flex
                    items-center
                    gap-2
                  "
                >

                  <span
                    className="
                      h-5
                      w-5
                      animate-spin
                      rounded-full
                      border-2
                      border-white/40
                      border-t-white
                    "
                    aria-hidden="true"
                  />


                  <span>
                    로그인 중
                  </span>

                </span>

              ) : (

                '로그인'

              )}

            </button>

          </form>


          {/* =========================================
              회원 메뉴
              ========================================= */}

          <nav
            className="
              mt-8
            "
            aria-label="회원 메뉴"
          >

            {/* 회원가입 */}

            <div
              className="
                flex
                justify-center
              "
            >

              <button
                type="button"
                className="
                  text-sm
                  font-medium
                  text-main
                  hover:underline
                "
                onClick={() =>
                  navigate(
                    RouteConfig
                      .Auth
                      .Signup
                      .fullPath,
                  )
                }
              >
                회원가입
              </button>

            </div>


            {/* 아이디 / 비밀번호 찾기 */}

            <div
              className="
                mt-4
                flex
                items-center
                justify-center
                text-sm
                text-gray-500
              "
            >

              <button
                type="button"
                className="
                  transition-colors
                  hover:text-main
                "
                onClick={() =>
                  navigate(
                    RouteConfig
                      .Auth
                      .FindId
                      .fullPath,
                  )
                }
              >
                아이디 찾기
              </button>


              <span
                className="
                  mx-3
                  text-gray-300
                "
                aria-hidden="true"
              >
                |
              </span>


              <button
                type="button"
                className="
                  transition-colors
                  hover:text-main
                "
                onClick={() =>
                  navigate(
                    RouteConfig
                      .Auth
                      .ResetPassword
                      .fullPath,
                  )
                }
              >
                비밀번호 찾기
              </button>

            </div>

          </nav>

        </div>

      </section>

    </main>
  );
}